import { useContext, useState } from 'react'
import { AdminThemeContext, type AdminTheme } from '../../hooks/useAdminTheme'

interface AdminThemeToggleProps {
  variant?: 'sidebar' | 'topbar' | 'login'
  label?: string
}

function readAdminTheme(): AdminTheme {
  if (typeof document !== 'undefined') {
    const t = document.documentElement.dataset.adminTheme
    if (t === 'dark' || t === 'light') return t
  }
  return 'dark'
}

/**
 * ADMIN-ONLY theme toggle. Uses the admin theme context (`bns-admin-theme`),
 * never the global customer theme. Scoped classes: `admin-theme-toggle*`.
 */
export function AdminThemeToggle({ variant = 'topbar', label }: AdminThemeToggleProps) {
  const ctx = useContext(AdminThemeContext)
  // Graceful fallback for isolated unit tests without provider.
  const [fallback, setFallback] = useState<AdminTheme>(() => readAdminTheme())
  const adminTheme = ctx ? ctx.adminTheme : fallback
  const isDark = adminTheme === 'dark'

  const toggle = () => {
    if (ctx) {
      ctx.toggleAdminTheme()
      return
    }
    const next: AdminTheme = fallback === 'light' ? 'dark' : 'light'
    setFallback(next)
    try {
      document.documentElement.dataset.adminTheme = next
      window.localStorage.setItem('bns-admin-theme', next)
    } catch {
      // ignore
    }
  }

  const className = [
    'admin-theme-toggle',
    variant === 'sidebar' ? 'admin-theme-toggle-sidebar' : '',
    variant === 'login' ? 'admin-theme-toggle-login' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      onClick={toggle}
      className={className}
      aria-label={isDark ? 'Aktifkan mode terang' : 'Aktifkan mode gelap'}
      aria-pressed={isDark}
      title={isDark ? 'Mode terang' : 'Mode gelap'}
    >
      <span aria-hidden="true" className="admin-theme-toggle-track">
        {isDark ? (
          <svg className="admin-theme-toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg className="admin-theme-toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </span>
      {label ? <span className="admin-theme-toggle-label">{label}</span> : null}
    </button>
  )
}
